/* eslint-disable local-rules/function-naming */
import { fees } from '@motorway/motorway-sale-rules';

import {
	BULK_BIDDING_CSV_FIELDS,
	EXISTING_CSV_FIELDS,
} from 'Components/pages/VehiclesList/FiltersHeader/BulkBidding/bulkBiddingCSV.const';
import { logger } from 'Services/logger/logger';
import { gbpCurrencyFormatter } from 'Utilities/formatters';
import { triggerDownload } from 'Utilities/helpers/triggerDownload';

export const currencyFormatter = gbpCurrencyFormatter({ maximumFractionDigits: 0, minimumFractionDigits: 0 });

const parseReservePrice = (reservePrice: unknown): number | null => {
	if (typeof reservePrice !== 'string') return null;

	// We only want to match ASCII digits (0-9), not Unicode digits from other languages
	// eslint-disable-next-line regexp/prefer-d
	const currencyWithoutFormatting = reservePrice.replaceAll(/[^0-9.]/g, '');
	const parsedReservePrice = Number.parseFloat(currencyWithoutFormatting);

	return Number.isNaN(parsedReservePrice) ? null : parsedReservePrice;
};

const appendBulkBiddingColumnsToCSV = (parsedData: Record<string, unknown>[]) =>
	parsedData.map((row) => {
		const reservePrice = parseReservePrice(row[EXISTING_CSV_FIELDS.RESERVE_PRICE]);
		const feeAtReservePrice = reservePrice === null ? '' : fees.calculateFee(reservePrice);

		return {
			...row,
			[BULK_BIDDING_CSV_FIELDS.FEE_AT_RESERVE_PRICE]:
				typeof feeAtReservePrice === 'number' ? currencyFormatter.format(feeAtReservePrice) : '',
			[BULK_BIDDING_CSV_FIELDS.BID_AMOUNT]: '',
		};
	});

const createCsvUrl = (csvContent: string): string => {
	// Prepend UTF-8 BOM for Excel compatibility
	const BOM = '\uFEFF';
	const blob = new Blob([BOM + csvContent], { type: 'text/csv; charset=utf-8' });
	return URL.createObjectURL(blob);
};

const appendBulkBiddingColumnAndDownload = async ({
	fileName,
	unprocessedBlob,
}: {
	fileName: string;
	unprocessedBlob: Blob;
}): Promise<void> => {
	const { default: Papa } = await import('papaparse');

	Papa.parse<Record<string, unknown>>(new File([unprocessedBlob], fileName), {
		complete: (parsed) => {
			// If there is no data in the CSV keep the behaviour the same as existing
			if (parsed.data.length === 0) {
				const url = URL.createObjectURL(unprocessedBlob);
				triggerDownload({ fileName, url });
				return;
			}

			const parsedDataWithAppendedBulkBiddingValues = appendBulkBiddingColumnsToCSV(parsed.data);
			const appendedCsv = Papa.unparse(parsedDataWithAppendedBulkBiddingValues, {
				delimiter: ',',
				header: true,
				quotes: false,
			});
			const appendedCsvUrl = createCsvUrl(appendedCsv);

			triggerDownload({ fileName, url: appendedCsvUrl });
		},
		error: (err) => {
			logger.error({
				error: err,
				message: 'Failed to append bulk bidding column to CSV',
				scope: 'appendBulkBiddingColumnAndDownload',
			});

			// Fallback to original CSV  to not disrupt download
			const url = URL.createObjectURL(unprocessedBlob);
			triggerDownload({ fileName, url });
		},
		header: true,
	});
};

export const downloadCsv = async ({
	content,
	filename,
	shouldAppendBulkBiddingColumn,
}: {
	content: string;
	filename: string;
	shouldAppendBulkBiddingColumn: boolean;
}): Promise<void> => {
	// Prepend UTF-8 BOM for Excel compatibility
	const BOM = '\uFEFF';
	const contentWithBOM = BOM + content;
	const contentBlob = new Blob([contentWithBOM], { type: 'text/csv; charset=utf-8' });
	const csvFileName = `${filename}.csv`;

	if (!shouldAppendBulkBiddingColumn) {
		const url = URL.createObjectURL(contentBlob);
		triggerDownload({ fileName: csvFileName, url });
		return;
	}

	await appendBulkBiddingColumnAndDownload({ fileName: csvFileName, unprocessedBlob: contentBlob });
};
